import { useEffect, useState } from "react";
import { Bar, BarChart, ResponsiveContainer, Tooltip, XAxis } from "recharts";
import { api, money, fmtDate } from "@/lib/api";
import { PageHead } from "@/pages/admin/AdminLayout";

const Kpi = ({ k, v, testid }) => (
  <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-5">
    <p className="text-[11px] font-mono-epl uppercase tracking-wider text-slate-500">{k}</p>
    <p data-testid={testid} className="mt-2 font-display font-bold text-2xl tracking-tight">{v}</p>
  </div>
);

const Box = ({ title, children }) => (
  <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-5">
    <p className="text-[11px] font-mono-epl uppercase tracking-wider text-slate-500 mb-4">{title}</p>
    {children}
  </div>
);

const Rows = ({ data, testid }) => (
  <div className="space-y-2" data-testid={testid}>
    {Object.keys(data).length === 0 && <p className="text-sm text-slate-500">No data yet</p>}
    {Object.entries(data).map(([k, v]) => <div key={k} className="flex justify-between text-sm"><span className="text-slate-300 capitalize">{k}</span><span className="font-mono-epl">{v}</span></div>)}
  </div>
);

export default function Overview() {
  const [d, setD] = useState(null);
  const load = () => api.get("/admin/overview").then((r) => setD(r.data));
  useEffect(() => {
    load();
  }, []);
  if (!d) return null;
  return (
    <div data-testid="admin-overview">
      <PageHead title={d.camp.name}><span className="text-xs text-slate-500">Closes {fmtDate(d.camp.registration_closes_at)}</span></PageHead>
      <div className="grid grid-cols-2 lg:grid-cols-6 gap-3">
        <Kpi k="Paid players" v={d.confirmed} testid="kpi-confirmed" />
        <Kpi k="Remaining" v={`${d.remaining} / ${d.camp.capacity}`} testid="kpi-remaining" />
        <Kpi k="Gross revenue" v={money(d.gross_cents)} testid="kpi-gross" />
        <Kpi k="GST component" v={money(d.gst_cents)} testid="kpi-gst" />
        <Kpi k="Refunds" v={money(d.refunds_cents)} testid="kpi-refunds" />
        <Kpi k="Net revenue" v={money(d.net_cents)} testid="kpi-net" />
      </div>
      {(d.held > 0 || d.unpaid > 0) && <p data-testid="overview-unpaid" className="mt-3 text-xs text-slate-400">{d.unpaid} unpaid registration(s) saved · {d.held} place(s) currently held in active checkouts.</p>}
      {d.payment_issues?.length > 0 && (
        <div data-testid="overview-payment-issues" className="mt-3 rounded-2xl border border-red-400/30 bg-red-400/[0.06] px-5 py-3 text-sm text-red-200">
          <p className="font-medium">{d.payment_issues.length} Stripe payment(s) need review</p>
          {d.payment_issues.slice(0, 5).map((i) => (
            <p key={i.id} className="text-xs mt-1">{i.at.slice(0, 16).replace("T", " ")} · {i.issue.replaceAll("_", " ")} · {i.email || "no email"} · {i.amount_cents != null ? `${money(i.amount_cents)} ${(i.currency || "").toUpperCase()}` : ""} · {i.payment_intent || i.session_id}
              <button data-testid={`resolve-issue-${i.id}`} onClick={() => api.post(`/admin/payment-issues/${i.id}/resolve`).then(load)} className="ml-2 underline">Mark resolved</button></p>
          ))}
        </div>
      )}
      <div className="mt-4 grid lg:grid-cols-3 gap-3">
        <div className="lg:col-span-2"><Box title="Confirmed registrations · last 30 days">
          <div className="h-48"><ResponsiveContainer><BarChart data={d.trend}><XAxis dataKey="date" tick={{ fill: "#64748b", fontSize: 10 }} tickFormatter={(s) => s.slice(5)} /><Tooltip contentStyle={{ background: "#0f1420", border: "1px solid #ffffff1a" }} /><Bar dataKey="count" fill="#ccff00" radius={[4, 4, 0, 0]} /></BarChart></ResponsiveContainer></div>
        </Box></div>
        <Box title="By group">
          <div className="space-y-3" data-testid="overview-by-group">
            {d.by_group.map((g) => (
              <div key={g.code}><div className="flex justify-between text-sm"><span>{g.name}</span><span className="font-mono-epl">{g.count}</span></div>
                <div className="mt-1 h-1.5 rounded-full bg-white/10"><div className="h-full rounded-full bg-[var(--accent)]" style={{ width: `${d.confirmed ? (g.count / d.confirmed) * 100 : 0}%` }} /></div></div>
            ))}
          </div>
        </Box>
        <Box title="Payment statuses (checkouts)"><Rows data={d.payment_statuses} testid="overview-payment-statuses" /></Box>
        <Box title="Paid registrations by source"><Rows data={d.by_source} testid="overview-sources" /></Box>
      </div>
    </div>
  );
}
